import { cn } from "../../lib/utils";

/** Design tokens shared with WelcomePage / Leave Management */
export const ui = {
  card: "bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60",
  cardHeader:
    "px-4 sm:px-6 py-3 border-b border-[var(--color-text-border)]/70 flex items-center justify-between gap-2 flex-wrap",
  pageTitle: "text-[28px] leading-tight font-bold text-foreground uppercase",
  pageSub: "text-sm text-[var(--color-text-secondary)] mt-1",
  sectionLabel:
    "text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider",
  input:
    "h-9 sm:h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm bg-white focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10",
  tableHead:
    "text-left text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30",
  primaryBtn:
    "inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white font-semibold text-sm hover:bg-[var(--color-accent)] transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
  filterBtn:
    "inline-flex items-center justify-center gap-1.5 h-9 px-2.5 sm:px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-[12px] sm:text-sm font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/40 transition cursor-pointer",
};

export function PageHeader({ title, action, className }) {
  return (
    <div
      className={cn(
        "mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4",
        className
      )}
    >
      <div>
        <h1 className={ui.pageTitle}>{title}</h1>
      </div>
      {action ? <div className="shrink-0 self-end sm:self-auto">{action}</div> : null}
    </div>
  );
}

export function ContentCard({ title, action, children, className, bodyClassName }) {
  return (
    <div className={cn(ui.card, "overflow-hidden", className)}>
      {title ? (
        <div className={ui.cardHeader}>
          <h2 className="text-[13px] sm:text-[15px] font-semibold text-foreground">{title}</h2>
          {action}
        </div>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search...", className }) {
  return (
    <input
      type="search"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={cn(ui.input, "w-full max-w-[280px]", className)}
    />
  );
}

export function LoadingState({ message = "Loading...", className }) {
  return (
    <div
      className={cn(
        "p-8 sm:p-12 text-center text-sm text-[var(--color-text-secondary)]",
        className
      )}
    >
      {message}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, message, className }) {
  return (
    <div className={cn("p-8 sm:p-12 text-center", className)}>
      {Icon ? (
        <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-[var(--color-workspace)]/60 grid place-items-center text-[var(--color-text-secondary)]">
          <Icon className="h-6 w-6" />
        </div>
      ) : null}
      {title ? <p className="text-sm font-semibold text-foreground">{title}</p> : null}
      {message ? (
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">{message}</p>
      ) : null}
    </div>
  );
}

export function AlertBanner({ type = "success", children, className }) {
  const styles = {
    success:
      "bg-[var(--color-success)]/10 border-[var(--color-success)]/25 text-[var(--color-success)]",
    error: "bg-[var(--color-error)]/10 border-[var(--color-error)]/25 text-[var(--color-error)]",
    info: "bg-[var(--color-accent)]/5 border-[var(--color-accent)]/20 text-foreground",
  };
  return (
    <div
      className={cn(
        "mb-4 p-3 sm:p-4 rounded-xl border text-sm font-semibold",
        styles[type] || styles.info,
        className
      )}
    >
      {children}
    </div>
  );
}

export function DataTable({ columns, children, minWidth = "760px", className }) {
  return (
    <div className={cn("hidden md:block overflow-x-auto", className)}>
      <table className="w-full text-sm" style={{ minWidth }}>
        <thead>
          <tr className={ui.tableHead}>
            {columns.map((col) => (
              <th key={col.key || col} className={cn("px-4 py-3 font-semibold", col.className)}>
                {col.label || col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-text-border)]/35">{children}</tbody>
      </table>
    </div>
  );
}
